import { useEffect, useMemo, useRef, useState } from "react";
import { collection, onSnapshot, query } from "firebase/firestore";
import "./App.css";
import CommandPanel from "./CommandPanel";
import DeploymentDashboard from "./DeploymentDashboard";
import { DEMO_ANALYSIS, DEMO_PACKETS_WITH_DEMO, DEMO_PEER_COUNT } from "./demoData";
import { db, isFirebaseConfigured, authReady } from "./firebase";
import { runDispatchAgent } from "./gemini";
import {
  fetchBackendHealth,
  runBackendDispatch,
  setBackendConnectivity,
} from "./omnimeshBackend";
import ResponderPanel from "./ResponderPanel";
import VictimPanel from "./VictimPanel";
import MeshBackground from "./MeshBackground";
import OmniMeshSeedDevPanel from "./OmniMeshSeedDevPanel";

const DEMO_MODE_KEY = "omnimesh_web_demo_mode";
const DISPATCH_ENGINE_KEY = "omnimesh_dispatch_engine";

const packetTimestamp = (p) => Number(p?.createdAt ?? p?.ts ?? 0);

const NAV_TABS = [
  { id: "home", label: "Home", mode: "Victim", indicator: "red" },
  { id: "field", label: "Field", mode: "Responder", indicator: "yellow" },
  { id: "command", label: "Command", mode: "Command", indicator: "blue" },
];

const parsePacket = (doc) => {
  const data = doc.data();
  const urgency = data.urgency ?? data.severity ?? "GREEN";
  const tsMs =
    typeof data.ts === "number"
      ? data.ts
      : typeof data.createdAt?.toMillis === "function"
        ? data.createdAt.toMillis()
        : typeof data.createdAt === "number"
          ? data.createdAt
          : Date.now();
  const signals =
    typeof data.signals === "string"
      ? data.signals
      : typeof data.signalSources === "string"
        ? data.signalSources
      : Array.isArray(data.signalSources)
        ? data.signalSources.join("·")
        : "LSTM·AUD·VIS";
  return {
    id: doc.id,
    urgency,
    injury: data.injury || "Unknown",
    confidence: data.confidence ?? 0,
    isAutoGenerated: Boolean(data.isAutoGenerated),
    lat: data.lat,
    lng: data.lng ?? data.lon,
    createdAt: tsMs,
    signals,
    isDemo: Boolean(data.isDemo),
    isTestData: Boolean(data.isTestData),
  };
};

const NavIcon = ({ id }) => {
  switch (id) {
    case "home":
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M3 11.5L12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1v-8.5z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "field":
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "command":
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="4" y="4" width="16" height="12" rx="1" stroke="currentColor" strokeWidth="2" />
          <path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
};

function OmniMeshAppContent() {
  const [activeNavId, setActiveNavId] = useState("home");
  const [demoMode, setDemoMode] = useState(() => {
    try {
      const v = localStorage.getItem(DEMO_MODE_KEY);
      return v === "1";
    } catch {
      return false;
    }
  });
  const [realPackets, setRealPackets] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [dispatchEngine, setDispatchEngine] = useState(() => {
    try {
      const stored = localStorage.getItem(DISPATCH_ENGINE_KEY);
      return stored === "backend" ? "backend" : "gemini";
    } catch {
      return "gemini";
    }
  });
  const [backendOnline, setBackendOnline] = useState(true);
  const [connectivityBusy, setConnectivityBusy] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [alertPacket, setAlertPacket] = useState(null);
  const previousRedCount = useRef(0);
  const seenRedIds = useRef(new Set());

  useEffect(() => {
    try {
      localStorage.setItem(DEMO_MODE_KEY, demoMode ? "1" : "0");
    } catch {
      /* ignore */
    }
    // Clear stale analysis between demo / real so the user never sees
    // demo zone assignments while inspecting real Firestore packets.
    setAnalysis(null);
    previousRedCount.current = 0;
  }, [demoMode]);

  useEffect(() => {
    try {
      localStorage.setItem(DISPATCH_ENGINE_KEY, dispatchEngine);
    } catch {
      /* ignore */
    }
  }, [dispatchEngine]);

  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 2000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (activeNavId !== "command" || dispatchEngine !== "backend") return undefined;
    let cancelled = false;
    fetchBackendHealth()
      .then((health) => {
        if (!cancelled) setBackendOnline(Boolean(health?.connectivity));
      })
      .catch((err) => {
        console.warn("[OmniMesh backend] health poll failed:", err?.message);
      });
    return () => {
      cancelled = true;
    };
  }, [activeNavId, dispatchEngine]);

  const toggleBackendConnectivity = async () => {
    if (connectivityBusy) return;
    setConnectivityBusy(true);
    try {
      const next = !backendOnline;
      const result = await setBackendConnectivity(next);
      setBackendOnline(Boolean(result?.connectivity));
    } catch (error) {
      console.error("[OmniMesh backend] connectivity toggle failed:", error);
    } finally {
      setConnectivityBusy(false);
    }
  };


  /**
   * Single feed for UI: Firestore rows + optional demo rows (deduped by id, real wins).
   * Sorted newest-first by timestamp.
   */
  const displayPackets = useMemo(() => {
    if (!demoMode) {
      return [...realPackets].sort((a, b) => packetTimestamp(b) - packetTimestamp(a));
    }
    const merged = [...realPackets, ...DEMO_PACKETS_WITH_DEMO];
    const seen = new Set();
    const unique = [];
    for (const p of merged) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      unique.push(p);
    }
    return unique.sort((a, b) => packetTimestamp(b) - packetTimestamp(a));
  }, [realPackets, demoMode]);

  useEffect(() => {
    console.log("REAL PACKETS:", realPackets.length);
    console.log("DEMO PACKETS:", DEMO_PACKETS_WITH_DEMO.length);
    console.log("DISPLAY PACKETS:", displayPackets.length);
    console.log("DEMO MODE:", demoMode);
  }, [realPackets, displayPackets, demoMode]);

  const redCount = useMemo(
    () => displayPackets.filter((p) => p.urgency === "RED").length,
    [displayPackets],
  );

  const yellowCount = useMemo(
    () => displayPackets.filter((p) => p.urgency === "YELLOW").length,
    [displayPackets],
  );

  /** Mesh peer estimate: demo uses a stable preset for recordings; real reflects observed packet count until mesh telemetry is wired. */
  const peerCountEstimate = useMemo(() => {
    if (demoMode) return DEMO_PEER_COUNT;
    return Math.max(displayPackets.length, 0);
  }, [displayPackets.length, demoMode]);

  const analyzePackets = async (packetList) => {
    if (!packetList.length) return;
    setAnalyzing(true);
    if (demoMode) {
      // Stable, deterministic response for recordings — keeps the on-demand
      // "Run analysis" UX identical to the real Gemini path without burning
      // quota or producing different copy on every press.
      await new Promise((resolve) => setTimeout(resolve, 1400));
      setAnalysis(DEMO_ANALYSIS);
      setAnalyzing(false);
      return;
    }
    try {
      const result =
        dispatchEngine === "backend"
          ? await runBackendDispatch(packetList)
          : await runDispatchAgent(packetList);
      setAnalysis(result);
    } catch (error) {
      console.error(
        dispatchEngine === "backend" ? "Backend dispatch error:" : "Gemini dispatch error:",
        error,
      );
      setAnalysis({
        critical_alert: "Dispatch analysis unavailable",
        analysis:
          error?.message ||
          "Unexpected error — tactical fallback: sort by RED→YELLOW→GREEN and verify mesh relays.",
        zone_assignments: [],
        estimated_casualties: "Unknown",
        priority_order: [],
        mode_used: dispatchEngine === "backend" ? "local" : undefined,
      });
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    if (!db) return undefined;
    let unsubscribe;
    let cancelled = false;
    authReady.then(() => {
      if (cancelled) return;
      const packetsQuery = query(collection(db, "packets"));
      unsubscribe = onSnapshot(packetsQuery, (snapshot) => {
        const nextPackets = snapshot.docs.map(parsePacket);
        console.log("[FirebaseFirestore] packets snapshot", {
          count: nextPackets.length,
          demoMode,
        });
        setRealPackets(nextPackets);
      });
    });
    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [demoMode]);

  useEffect(() => {
    if (redCount > previousRedCount.current) {
      analyzePackets(displayPackets);
    }
    previousRedCount.current = redCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPackets, redCount]);

  useEffect(() => {
    const redPackets = displayPackets.filter((p) => p.urgency === "RED");
    const currentIds = new Set(redPackets.map((p) => p.id));
    const fresh = redPackets.find((p) => !seenRedIds.current.has(p.id));
    if (fresh && seenRedIds.current.size > 0) {
      setAlertPacket(fresh);
      const t = setTimeout(() => setAlertPacket(null), 2500);
      return () => clearTimeout(t);
    }
    seenRedIds.current = currentIds;
    return undefined;
  }, [displayPackets]);

  const [clock, setClock] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!isFirebaseConfigured) {
    return (
      <div className="app-root firebase-setup-error">
        <h1>Firebase is not configured in this build</h1>
        <p>
          <code>REACT_APP_FIREBASE_*</code> values were missing when <code>npm run build</code> ran, so the app cannot
          connect to Firestore. Create <code>web/.env</code> from your Firebase project (Project settings → Your apps →
          SDK snippet), then rebuild.
        </p>
        <p className="firebase-setup-hint">
          For GitHub Actions / CI, add those variables as repository secrets and pass them into the build step — they are
          baked in at compile time, not read from Hosting at runtime.
        </p>
      </div>
    );
  }

  const activeTab = NAV_TABS.find((t) => t.id === activeNavId) ?? NAV_TABS[0];
  const mode = activeTab.mode;
  const isLight = mode === "Victim";
  const stripRedPulse = redCount > 0;
  const stripYellow = !stripRedPulse && yellowCount > 0;

  const timeText = clock.toLocaleTimeString("en-GB", { hour12: false });
  const timezoneText = clock
    .toLocaleTimeString("en-IN", { timeZoneName: "shortOffset" })
    .split(" ")
    .slice(-1)[0]
    .replace("GMT", "IST ");

  if (showSplash) {
    return <SplashOverlay />;
  }

  return (
    <div className={`app-root ${isLight ? "theme-light" : "theme-dark"}`}>
      <div
        className={`system-status-strip ${stripRedPulse ? "status-red flash" : stripYellow ? "status-yellow" : "status-blue"}`}
      />
      <header className={`om-topbar ${isLight ? "om-topbar-light" : ""}`}>
        <div className="om-topbar-left">
          <>
            <span className="om-brand">OMNIMESH</span>
            <span className="om-pipe">|</span>
            <span className="om-screen">{mode.toUpperCase()}</span>
          </>
        </div>
        <div className="om-topbar-right">
          {isLight ? (
            <>
              <button
                type="button"
                className={`om-demo-pill ${demoMode ? "om-demo-pill--on" : ""}`}
                onClick={() => setDemoMode(!demoMode)}
                aria-pressed={demoMode}
              >
                {demoMode ? "Demo" : "Real"}
              </button>
            </>
          ) : (
            <>
              {mode === "Responder" ? <span className="om-data-label">DATA</span> : null}
              <div className="demo-real-toggle" role="group" aria-label="Data source mode">
                <button
                  type="button"
                  className={`demo-real-btn ${!demoMode ? "active" : ""}`}
                  onClick={() => setDemoMode(false)}
                >
                  REAL
                </button>
                <button
                  type="button"
                  className={`demo-real-btn ${demoMode ? "active" : ""}`}
                  onClick={() => setDemoMode(true)}
                >
                  DEMO
                </button>
              </div>
              <div className={`om-peer-pill ${peerCountEstimate > 0 ? "active" : ""}`}>
                <span className="om-peer-dot" />
                <span>{peerCountEstimate} PEERS</span>
              </div>
              <span className="om-clock">{timeText}</span>
              {mode === "Command" ? <span className="om-timezone">{timezoneText}</span> : null}
              {mode === "Command" && dispatchEngine === "backend" ? (
                <button
                  type="button"
                  className={`om-connectivity-toggle ${backendOnline ? "om-connectivity-toggle--on" : "om-connectivity-toggle--off"}`}
                  onClick={toggleBackendConnectivity}
                  disabled={connectivityBusy}
                  aria-pressed={backendOnline}
                  title="Toggle OmniMesh AI backend uplink (demo hybrid routing)"
                >
                  <span className="om-connectivity-toggle__label">Connectivity</span>
                  <span className="om-connectivity-toggle__state">{backendOnline ? "ONLINE" : "OFFLINE"}</span>
                </button>
              ) : null}
            </>
          )}
        </div>
      </header>

      <main className="panel-shell">
        <MeshBackground mode={mode} />
        {mode === "Victim" && (
          <VictimPanel
            packets={displayPackets}
            peerCount={peerCountEstimate}
            demoMode={demoMode}
            onSwitchToField={() => setActiveNavId("field")}
          />
        )}
        {mode === "Responder" && (
          <ResponderPanel
            packets={displayPackets}
            demoMode={demoMode}
            analysis={analysis}
            analyzing={analyzing}
            dispatchEngine={dispatchEngine}
            onAnalyze={() => analyzePackets(displayPackets)}
          />
        )}
        {mode === "Command" && (
          <CommandPanel
            packets={displayPackets}
            peerCountEstimate={peerCountEstimate}
            demoMode={demoMode}
            analysis={analysis}
            analyzing={analyzing}
            dispatchEngine={dispatchEngine}
            onDispatchEngineChange={setDispatchEngine}
            onAnalyze={() => analyzePackets(displayPackets)}
          />
        )}
      </main>

      {alertPacket ? (
        <div className="global-alert-cascade" role="status" aria-live="polite">
          <div className="global-alert-badge">RED</div>
          <div className="global-alert-copy">
            <div>{alertPacket.injury}</div>
            <div>
              GPS {(alertPacket.lat ?? 0).toFixed(4)}, {(alertPacket.lng ?? alertPacket.lon ?? 0).toFixed(4)}
            </div>
          </div>
        </div>
      ) : null}

      {process.env.NODE_ENV === "development" ? <OmniMeshSeedDevPanel /> : null}

      <nav className="bottom-nav" role="tablist" aria-label="OmniMesh modes">
        {NAV_TABS.map((tab) => {
          const active = tab.id === activeNavId;
          const showAlert = tab.id === "home" && redCount > 0;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`bottom-nav-item ${active ? "active" : ""} indicator-${tab.indicator}`}
              onClick={() => setActiveNavId(tab.id)}
            >
              <span className="active-indicator" aria-hidden />
              <span className="icon-wrap">
                <NavIcon id={tab.id} />
                {showAlert ? <span className="alert-dot" aria-hidden /> : null}
              </span>
              <span className="bottom-nav-label">{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}

function SplashOverlay() {
  return (
    <div className="splash-root">
      <MeshBackground mode="Victim" />
      <div className="splash-center">
        <div className="splash-ring" />
        <div className="splash-logo">
          <span />
          <span />
        </div>
        <div className="splash-wordmark">OMNIMESH</div>
      </div>
    </div>
  );
}

export default function App() {
  const isDashboard = window.location.pathname === "/dashboard";
  if (isDashboard) {
    return <DeploymentDashboard />;
  }
  return <OmniMeshAppContent />;
}
